import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { computeColor } from '../utils/colors';
import { alignTypeToJustifyContent } from '../utils/layout';

import { textColorsFallback } from '../assets/colors';
import { Icon } from '../icon';
import type { TTextLeaf as TextLeaf } from '../textLeaf/Text.types';
import type { TIcon } from '../types';
import * as styles from './Button.css';
import type { ButtonElementProps } from './Button.type';

const BUTTON_DEFAULTS = {
  margin: '0px 0px 0px 0px',
  cursor: 'pointer',
  borderRadius: '0.5rem',
  // boxShadow: "0 10px 20px -10px #6E79D6",
  borderColor: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  boxShadow: 'unset',
  backgroundColor: 'rgb(236, 72, 153)',
};

type ButtonIconProps = React.HTMLAttributes<HTMLDivElement> & {
  icon: TIcon;
  color?: string;
  className?: string;
  onClick?: () => void;
  hoverColor?: string;
  size?: string;
};

export function ButtonIcon({ icon, color, className, onClick, hoverColor, size, ...rest }: ButtonIconProps) {
  if (!icon) return null;

  switch (icon.type) {
    case 'icon':
      return (
        // biome-ignore lint/a11y/useValidAriaRole: role=none is used to ignore the element for accessibility purposes
        <div
          {...rest}
          contentEditable={false}
          className={classNames('pt-[0.5px]', className)}
          style={
            {
              '--icon-color': computeColor(color, textColorsFallback),
              '--hover-icon-color': hoverColor || 'var(--icon-color)',
              '--icon-size': size,
            } as CSSProperties
          }
          role="none"
          onClick={onClick}
        >
          <Icon type={icon.iconType} name={icon.iconName} size={size ? Number.parseInt(size) : undefined} />
        </div>
      );
    case 'emoji':
      return (
        // biome-ignore lint/a11y/useValidAriaRole: role=none is used to ignore the element for accessibility purposes
        <div {...rest} contentEditable={false} className={className} role="none" onClick={onClick}>
          {icon.content}
        </div>
      );
    default:
      return null;
  }
}

function ButtonComponent(props: ButtonElementProps) {
  const { element, children, editable, attributes, onClick, disabled, isClientSideNavigation, type } = props;
  const {
    buttonType,
    color,
    icon,
    iconPlacement = 'left',
    customAttributes,
    customTextColor,
    customBackgroundColor,
    variant,
    width,
    maxWidth,
    align,
    margin,
    padding,
    borderRadius,
    borderColor,
    borderWidth,
    borderStyle,
    shadow,
    iconColor,
    iconSize,
    url,
    open_in_new_tab,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverCustomBackgroundColor,
    hoverCustomTextColor,
    hoverIconColor,
    fontFamily,
    fontSize,
    mobileFontSize,
    fontWeight,
    renderAsDiv,
  } = element.attrs || {};

  const isTextLeafChildrenEmpty = (element?.content?.[0] as unknown as TextLeaf)?.text === '';
  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? BUTTON_DEFAULTS.margin).toString().split(' ');

  const commonProps = {
    ...customAttributes,
    className: classNames(styles.button, styles.themeableButton({ variant }), customAttributes?.className),
    onClick,
    style: {
      '--text-color': customTextColor || computeColor(color, textColorsFallback),
      padding,
      '--border-color': borderColor ?? BUTTON_DEFAULTS.borderColor,
      '--border-width': borderWidth ?? BUTTON_DEFAULTS.borderWidth,
      '--border-style': borderStyle ?? BUTTON_DEFAULTS.borderStyle,
      '--border-radius': borderRadius ?? BUTTON_DEFAULTS.borderRadius,
      '--button-background-color': customBackgroundColor || BUTTON_DEFAULTS.backgroundColor,
      boxShadow: shadow ?? BUTTON_DEFAULTS.boxShadow,
      width,
      maxWidth,
      '--hover-border-color': hoverBorderColor || 'var(--border-color)',
      '--hover-border-style': hoverBorderStyle || 'var(--border-style)',
      '--hover-border-width': hoverBorderWidth || 'var(--border-width)',
      '--hover-border-radius': hoverBorderRadius || 'var(--border-radius)',
      '--hover-background-color': hoverCustomBackgroundColor || 'var(--button-background-color)',
      '--hover-text-color': hoverCustomTextColor || 'var(--text-color)',
      '--horizontal-alignment': align && alignTypeToJustifyContent(align),
      '--margin-top': marginTop,
      '--margin-right': marginRight,
      '--margin-bottom': marginBottom,
      '--margin-left': marginLeft,
      '--margin': margin ?? BUTTON_DEFAULTS.margin,
      '--font-family': fontFamily,
      ...(fontSize && { '--font-size': fontSize }),
      ...(mobileFontSize && { '--mobile-font-size': mobileFontSize }),
      ...(fontWeight && { '--font-weight': fontWeight }),
    } as CSSProperties,
    ...attributes,
  };

  const iconContent = (
    <>
      {icon && iconPlacement === 'left' ? (
        <ButtonIcon
          icon={icon}
          color={iconColor}
          size={iconSize}
          hoverColor={hoverIconColor}
          data-placement={iconPlacement}
          className={classNames(styles.icon, {
            [styles.iconWithEmptyText]: editable && isTextLeafChildrenEmpty,
          })}
        />
      ) : null}
      {children}
      {icon && iconPlacement === 'right' ? (
        <ButtonIcon
          color={iconColor}
          hoverColor={hoverIconColor}
          icon={icon}
          size={iconSize}
          data-placement={iconPlacement}
          className={classNames(styles.icon, {
            [styles.iconWithEmptyText]: editable && isTextLeafChildrenEmpty,
          })}
        />
      ) : null}
    </>
  );

  if (renderAsDiv) {
    return (
      <div
        {...commonProps}
        // biome-ignore lint/a11y/useSemanticElements: div with role="button" is intentional to avoid HTML nesting issues
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.key === 'Enter' || e.key === ' ') && onClick && !disabled) {
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent);
          }
        }}
      >
        {iconContent}
      </div>
    );
  }

  const ButtonWrapper = isClientSideNavigation ? 'button' : 'a';

  return (
    <ButtonWrapper
      {...commonProps}
      type={buttonType || type || 'button'}
      href={url}
      target={open_in_new_tab ? '_blank' : undefined}
      disabled={disabled}
    >
      {iconContent}
    </ButtonWrapper>
  );
}

export function ButtonElement(props: ButtonElementProps) {
  const { children } = props;

  return <ButtonComponent {...props}>{children}</ButtonComponent>;
}
